import { Github, Instagram, Linkedin, Mail, MessageCircle, Phone } from 'lucide-react';
import type { ReactNode } from 'react';

import { useI18n } from '../../../shared/i18n';

export interface ContactChannelsProps {
  email?: string | undefined;
  phone?: string | undefined;
  /** Digits only, country code included, no leading "+". */
  whatsapp?: string | undefined;
  linkedin?: string | undefined;
  github?: string | undefined;
  instagram?: string | undefined;
}

/**
 * Each channel renders only when its setting is non-empty (FR-MSG-01) — no
 * "coming soon" placeholders for a channel nobody configured.
 */
export function ContactChannels(props: ContactChannelsProps) {
  const { t } = useI18n();
  const items: { href: string; icon: ReactNode; label: string; ltr?: boolean }[] = [];

  if (props.email) {
    items.push({
      href: `mailto:${props.email}`,
      icon: <Mail className="size-5" aria-hidden="true" />,
      label: props.email,
      ltr: true,
    });
  }
  if (props.phone) {
    items.push({
      href: `tel:${props.phone}`,
      icon: <Phone className="size-5" aria-hidden="true" />,
      label: props.phone,
      // A phone number is digits and punctuation only — no strong-direction
      // character to anchor it — so in an RTL paragraph its leading "+" and
      // surrounding parens/dashes can visually reorder exactly like the
      // "/projects" eyebrow bug did. Isolate it the same way.
      ltr: true,
    });
  }
  if (props.whatsapp) {
    items.push({
      href: `https://wa.me/${props.whatsapp}`,
      icon: <MessageCircle className="size-5" aria-hidden="true" />,
      label: t('common.contact.whatsapp'),
    });
  }
  if (props.linkedin) {
    items.push({
      href: props.linkedin,
      icon: <Linkedin className="size-5" aria-hidden="true" />,
      label: 'LinkedIn',
    });
  }
  if (props.github) {
    items.push({
      href: props.github,
      icon: <Github className="size-5" aria-hidden="true" />,
      label: 'GitHub',
    });
  }
  if (props.instagram) {
    items.push({
      href: props.instagram,
      icon: <Instagram className="size-5" aria-hidden="true" />,
      label: 'Instagram',
    });
  }

  if (items.length === 0) return null;

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold text-text">{t('common.contact.channels')}</h2>
      <ul className="flex flex-col gap-1">
        {items.map((item) => (
          <li key={item.href} className="border-b border-border py-3 first:pt-0 last:border-b-0">
            <a
              href={item.href}
              target={item.href.startsWith('http') ? '_blank' : undefined}
              rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="group flex items-center gap-3"
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-accent-subtle text-accent">
                {item.icon}
              </span>
              <span
                dir={item.ltr ? 'ltr' : undefined}
                className="text-text-muted transition-colors group-hover:text-text"
              >
                {item.label}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
